import React, { useMemo, useState } from "react";
import { Button } from "../ui/button";
import { Pick } from "../loans/LoanFormDialog";
import { Reminders } from "../../lib/api";
import { formatIndianDate } from "../../lib/format";
import { TYPE_LABEL } from "./constants";
import { toast } from "sonner";
import { Loader2, Search, ListPlus } from "lucide-react";

const rs = (p) => `₹${(p / 100).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
const NOF = { clf_id: "all", vo_id: "all", shg_id: "all", member_id: "all" };

export const QueueBuilder = ({ refs, onQueued }) => {
  const [f, setF] = useState(NOF);
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState("");
  const setFilter = (k, v) => setF((p) => ({ ...p, [k]: v, ...(k === "clf_id" ? { vo_id: "all", shg_id: "all", member_id: "all" } : k === "vo_id" ? { shg_id: "all", member_id: "all" } : k === "shg_id" ? { member_id: "all" } : {}) }));
  const sel = (list, all) => [{ value: "all", label: all }, ...list.map((x) => ({ value: x.id, label: x.name }))];
  const vos = useMemo(() => refs.vos.filter((x) => f.clf_id === "all" || x.clf_id === f.clf_id), [refs.vos, f.clf_id]);
  const shgs = useMemo(() => refs.shgs.filter((x) => f.vo_id === "all" ? vos.some((v) => v.id === x.vo_id) : x.vo_id === f.vo_id), [refs.shgs, vos, f.vo_id]);
  const members = useMemo(() => refs.members.filter((x) => f.shg_id === "all" ? shgs.some((s) => s.id === x.shg_id) : x.shg_id === f.shg_id), [refs.members, shgs, f.shg_id]);

  const run = async (dry) => {
    setBusy(dry ? "preview" : "queue");
    try {
      const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v !== "all"));
      const out = await Reminders.build({ ...params, dry_run: dry });
      setRes(out);
      if (!dry) { toast.success(`${out.ready.length} reminder(s) added to the queue`); onQueued(); }
    } catch (e) { toast.error(e.message); } finally { setBusy(""); }
  };

  return (
    <div className="space-y-4 rounded-2xl border border-emerald-950/10 bg-white p-4" data-testid="queue-builder">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-6">
        <Pick testId="queue-filter-clf" value={f.clf_id} options={sel(refs.clfs, "All CLFs")} onChange={(v) => setFilter("clf_id", v)} />
        <Pick testId="queue-filter-vo" value={f.vo_id} options={sel(vos, "All VOs")} onChange={(v) => setFilter("vo_id", v)} />
        <Pick testId="queue-filter-shg" value={f.shg_id} options={sel(shgs, "All SHGs")} onChange={(v) => setFilter("shg_id", v)} />
        <Pick testId="queue-filter-member" value={f.member_id} options={sel(members, "All members")} onChange={(v) => setFilter("member_id", v)} />
        <Button variant="outline" className="h-10" disabled={!!busy} onClick={() => run(true)} data-testid="queue-preview-btn">
          {busy === "preview" ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Search className="mr-1.5 h-4 w-4" />}Check who is due</Button>
        <Button className="h-10 bg-[#0F4C3A] text-white hover:bg-emerald-900" disabled={!!busy || !res?.dry_run || !res.ready.length} onClick={() => run(false)} data-testid="queue-add-btn">
          {busy === "queue" ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <ListPlus className="mr-1.5 h-4 w-4" />}Add {res?.dry_run ? res.ready.length : ""} to queue</Button>
      </div>
      {res && (
        <div className="grid gap-4 text-sm lg:grid-cols-2" data-testid="queue-builder-result">
          <div>
            <div className="mb-1 font-semibold text-emerald-950" data-testid="queue-ready-count">{res.dry_run ? "Ready to queue" : "Added to queue"}: {res.ready.length}</div>
            {!res.enabled_types.length && <p className="text-xs text-amber-800" data-testid="queue-all-off">All reminder types are switched off. Turn them on in Settings.</p>}
            {res.type_off_count > 0 && res.enabled_types.length > 0 && <p className="text-xs text-emerald-900/60">{res.type_off_count} loan(s) skipped because that reminder type is off.</p>}
            <ul className="mt-1 space-y-1">{res.ready.map((r) => (
              <li key={`${r.loan_id}-${r.type}`} className="rounded-lg bg-emerald-50 px-2 py-1 text-xs">{r.recipient_name} · {r.phone_masked} · {r.loan_number} #{r.instalment_no} · {TYPE_LABEL[r.type]} · due {formatIndianDate(r.due_date)} · {rs(r.emi_paise)}</li>
            ))}</ul>
          </div>
          <div>
            <div className="mb-1 font-semibold text-emerald-950" data-testid="queue-excluded-count">Not included: {res.excluded.length}</div>
            <ul className="space-y-1">{res.excluded.map((r) => (
              <li key={`${r.loan_id}-${r.type}`} className="rounded-lg bg-rose-50 px-2 py-1 text-xs text-rose-900">{r.recipient_name || "—"} · {r.loan_number} · {TYPE_LABEL[r.type]} — <strong>{r.reason}</strong></li>
            ))}</ul>
          </div>
        </div>
      )}
    </div>
  );
};
